// LageHeader — Ebene 1 "Die Lage" (Auftrag H Schritt 2, DESIGN-V3-LAGEBILD §3+§4.2).
//
// Ersetzt FokusPanel-Zeile + actions/hero-Bento-Zeile: EIN Serif-Satz
// (buildLageSatz aus derive-actions), EINE Hero-Zahl (Cash konsolidiert,
// Fallback Netto-4W, useCountUp), 3 Micro-KPIs, Handeln-Liste (max 3,
// Clay-Dots, Rest hinter "alle anzeigen"). Datenlogik-Pass — visuelle
// Feinpolitur (Glow/Radial-Ambient) macht die Cowork-Screenshot-Session.
//
// Signalfarben-Regel (§3): Clay = handeln, Amber = beobachten, sonst nichts
// Buntes. Serif (font-voice) NUR fuer Lage-Satz + Hero-Zahl.

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { formatEurRound, useCountUp } from "../hooks/useCountUp";
import { flashClass, flashStyle, useFlashOnChange } from "../hooks/useFlashOnChange";
import type {
  CashTotalKpi,
  LiquiPlanungResponse,
  MahnungenEingegangenResponse,
  PipelineResponse,
  UmsatzLiveResponse,
} from "../lib/api";
import type { ActionDrawer, Netto4W, TodayAction } from "../lib/derive-actions";
import { buildLageSatz, deriveUeberfaelligDxGk } from "../lib/derive-actions";
import type { Severity } from "../lib/severity";

const MAX_VISIBLE = 3;

// Handeln-Dots: Clay = handeln (critical), Amber = beobachten (warn),
// ok-Punkte bekommen den gedimmten Ton — bewusst KEIN drittes Buntsignal.
const DOT: Record<Severity, string> = {
  critical: "bg-signal-clay",
  warn: "bg-signal-amber",
  ok: "bg-leitstand-dim/60",
};

interface Props {
  actions: TodayAction[];
  netto: Netto4W;
  cash?: CashTotalKpi | null;
  umsatzLive?: UmsatzLiveResponse | null;
  mahnungen?: MahnungenEingegangenResponse | null;
  pipeline?: PipelineResponse | null;
  /** Cash-Graph (K7): Reichweite, Deckung, Burn — für Reichweite-KPI +
   *  Kausalkette. */
  liqui?: LiquiPlanungResponse | null;
  loading?: boolean;
  onOpenDrawer?: (drawer: ActionDrawer) => void;
}

/** K7-Kausalkette: EIN Satz, der Cash → Reserve → Reichweite → größter
 *  Hebel verknüpft. So sieht Kevin das Zusammenspiel der Zahlen, statt sie
 *  einzeln zusammenrechnen zu müssen. Gibt null, wenn die Basis fehlt. */
function buildKausalkette(
  cashNum: number | null,
  liqui: LiquiPlanungResponse | null | undefined,
  ueberfaelligSum: number,
): string | null {
  if (cashNum == null || liqui == null) return null;
  const deckung = liqui.deckung_pct;
  const reichweite = liqui.reichweite_tage;
  if (deckung == null && reichweite == null) return null;

  const teile: string[] = [];
  if (deckung != null) teile.push(`${Math.round(deckung)} % der 3-Monats-Reserve`);
  if (reichweite != null) teile.push(`Reichweite ~${reichweite} Tage`);
  let satz = `Cash deckt ${teile.join(" · ")}.`;

  // Größter Hebel: überfällige Forderungen in Reichweite-Tage übersetzen.
  const burn = Number.parseFloat(liqui.monats_burn_rate_eur);
  if (ueberfaelligSum > 0 && Number.isFinite(burn) && burn > 0) {
    const tage = Math.round(ueberfaelligSum / (burn / 30));
    if (tage >= 1) {
      satz += ` Größter Hebel: ${formatEurRound(ueberfaelligSum)} überfällig — eingezogen verlängert das die Reichweite um ~${tage} Tage.`;
    }
  }
  return satz;
}

function parseEur(v: string | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** Hero-Zahl: Cash konsolidiert; bis EBICS liefert, ist Netto-4W die
 *  ehrlichste eine Zahl (Spec §3 Ebene 1). */
function resolveHero(
  cash: CashTotalKpi | null | undefined,
  netto: Netto4W,
): { value: number | null; label: string } {
  const cashNum = cash?.status === "ok" ? parseEur(cash.total_eur) : null;
  if (cashNum != null) {
    return { value: cashNum, label: "Cash konsolidiert · xholding+drainx+gundk" };
  }
  return {
    value: netto.netto_eur,
    label:
      netto.hinweis != null
        ? `Netto 4 Wochen · ${netto.hinweis}`
        : "Netto 4 Wochen · Eingang − Ausgang",
  };
}

export default function LageHeader({
  actions,
  netto,
  cash,
  umsatzLive,
  mahnungen,
  pipeline,
  liqui,
  loading,
  onOpenDrawer,
}: Props) {
  const navigate = useNavigate();
  const [alleOffen, setAlleOffen] = useState(false);

  const hero = resolveHero(cash, netto);
  const animated = useCountUp(hero.value);
  // K7: der Hero pulst 1×, wenn sich Cash/Netto nach Refetch/Edit ändert.
  const heroFlash = useFlashOnChange(hero.value);
  const satz = buildLageSatz(actions, netto);

  const ueberfaellig = deriveUeberfaelligDxGk(umsatzLive);
  const mahnOffen = mahnungen?.n_offen ?? 0;
  const mahnTotal = parseEur(mahnungen?.total_offen_eur);
  const angeboteOffen = pipeline?.available ? pipeline.n_offers_open : null;

  // K7-Cash-Graph: Reichweite als verknüpfende Zahl + Kausalkette.
  const cashNum = cash?.status === "ok" ? parseEur(cash.total_eur) : null;
  const reichweite = liqui?.reichweite_tage ?? null;
  const kausalkette = buildKausalkette(cashNum, liqui, ueberfaellig.sum);

  const visible = alleOffen ? actions : actions.slice(0, MAX_VISIBLE);
  const hidden = actions.length - MAX_VISIBLE;

  return (
    <section aria-label="Die Lage" className="space-y-3">
      {/* Lage-Satz — die Serif-Stimme (§3 Ebene 1). */}
      <p className="font-voice italic text-base sm:text-lg text-leitstand-text/90 max-w-3xl">
        {loading && actions.length === 0 ? "…" : satz}
      </p>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        {/* Hero-Zahl + Micro-KPIs. */}
        <div className="min-w-0">
          {loading && hero.value == null ? (
            <div className="h-14 w-64 animate-pulse rounded bg-leitstand-panel" />
          ) : (
            <div
              className={`font-voice text-5xl sm:text-6xl tracking-tight text-leitstand-text tabular-nums ${flashClass(heroFlash)}`}
            >
              {formatEurRound(animated)}
            </div>
          )}
          <div className="label-tracked mt-1">{hero.label}</div>

          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1">
            <MicroKpi
              label="Reichweite"
              value={reichweite != null ? `${reichweite} Tage` : "—"}
              warn={reichweite != null && reichweite < 90}
              flashSource={reichweite}
              flowIndex={0}
            />
            <MicroKpi
              label="Überfällig Dx+G+K"
              value={ueberfaellig.sum > 0 ? formatEurRound(ueberfaellig.sum) : "—"}
              warn={ueberfaellig.sum > 25_000}
              flashSource={ueberfaellig.sum}
              flowIndex={1}
            />
            <MicroKpi
              label="Mahnungen gegen uns"
              value={
                mahnOffen > 0
                  ? `${mahnOffen}${mahnTotal != null ? ` · ${formatEurRound(mahnTotal)}` : ""}`
                  : "0"
              }
              warn={mahnOffen > 0}
              flashSource={mahnTotal}
              flowIndex={2}
            />
            <MicroKpi
              label="Angebote offen"
              value={angeboteOffen != null ? String(angeboteOffen) : "—"}
              flashSource={angeboteOffen}
              flowIndex={3}
            />
          </dl>

          {/* K7-Kausalkette: das Zusammenspiel in einem Satz. */}
          {kausalkette && (
            <p className="mt-2 max-w-xl text-xs text-leitstand-dim">{kausalkette}</p>
          )}
        </div>

        {/* Handeln-Liste (max 3 sichtbar, Clay = handeln). */}
        <div className="w-full lg:w-[26rem] shrink-0">
          {actions.length === 0 && !loading && (
            <p className="text-xs text-leitstand-dim">
              Keine offenen Aktionen — alles im grünen Bereich.
            </p>
          )}
          <ul className="space-y-1">
            {visible.map((a) => {
              const clickable = a.to != null || (a.drawer != null && onOpenDrawer != null);
              const handle = () => {
                if (a.to != null) {
                  navigate(a.to);
                } else if (a.drawer != null && onOpenDrawer != null) {
                  onOpenDrawer(a.drawer);
                }
              };
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={handle}
                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left transition-colors ${
                      clickable ? "hover:bg-leitstand-panel cursor-pointer" : "cursor-default"
                    }`}
                  >
                    <span
                      aria-hidden
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[a.severity]}`}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-leitstand-text">
                      {a.titel}
                    </span>
                    {a.betrag_eur != null && (
                      <span className="num-mono shrink-0 text-xs text-leitstand-dim">
                        {formatEurRound(a.betrag_eur)}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setAlleOffen((v) => !v)}
              className="mt-1 px-2 text-[11px] text-leitstand-dim hover:text-leitstand-text"
            >
              {alleOffen ? "weniger anzeigen" : `alle anzeigen (${actions.length})`}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function MicroKpi({
  label,
  value,
  warn,
  flashSource,
  flowIndex = 0,
}: {
  label: string;
  value: string;
  warn?: boolean;
  /** K7: numerischer Rohwert für den Change-Flash (optional). */
  flashSource?: number | null;
  /** Flow-Kaskade: Position in der KPI-Reihe → gestaffelter Flash. */
  flowIndex?: number;
}) {
  const flash = useFlashOnChange(flashSource);
  return (
    <div>
      <dt className="label-tracked">{label}</dt>
      <dd
        className={`num-mono mt-0.5 text-sm ${warn ? "text-signal-amber" : "text-leitstand-text"} ${flashClass(flash)}`}
        style={flashStyle(flash, flowIndex * 70)}
      >
        {value}
      </dd>
    </div>
  );
}
